import React, { useState } from 'react';
import { Smartphone, QrCode, Copy, ExternalLink, AlertCircle, CheckCircle } from 'lucide-react';

const WaveQRPaymentSimple = ({ 
  schoolId, 
  amount = 10000,
  description = "Abonnement mensuel Jangal",
  waveQrCodeUrl
}) => {
  const [qrError, setQrError] = useState('');

  const handleImageError = () => {
    setQrError('Erreur de chargement du QR code. Veuillez recharger la page.');
  };

  if (!waveQrCodeUrl) {
    return (
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-12 text-center">
          <QrCode className="mx-auto text-gray-300 mb-6" size={80} />
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            QR Code Non Disponible
          </h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Le système de paiement Wave n'est pas encore configuré par l'administrateur.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 max-w-md mx-auto">
            <div className="flex items-start mb-4">
              <AlertCircle className="text-yellow-600 mr-3 mt-1" size={24} />
              <div>
                <h4 className="font-bold text-yellow-800 text-lg mb-2">Que faire ?</h4>
                <ul className="text-yellow-700 space-y-2 text-left">
                  <li>• Contactez votre administrateur système</li>
                  <li>• Demandez la configuration du QR code Wave</li>
                  <li>• Utilisez un autre moyen de paiement temporairement</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
      
      {/* En-tête */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0">
            <h2 className="text-2xl md:text-3xl font-bold flex items-center">
              <Smartphone className="mr-3" size={32} />
              Paiement Abonnement avec Wave
            </h2>
            <p className="text-purple-200 mt-2">Scannez le QR code avec votre application Wave</p>
          </div>
          
          <div className="text-right">
            <p className="text-3xl font-bold">{amount.toLocaleString('fr-FR')} FCFA</p>
            <p className="text-purple-200">Abonnement mensuel</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Colonne gauche : Comment ça marche */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-6 shadow-sm">
              <h3 className="font-bold text-purple-900 text-xl mb-5 flex items-center">
                <Smartphone className="mr-3 text-purple-600" size={24} />
                Comment ça marche ?
              </h3>
              
              <div className="space-y-5">
                <div className="flex items-start">
                  <div className="bg-purple-100 text-purple-800 rounded-lg w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">1</div>
                  <div>
                    <p className="font-semibold text-purple-800 text-lg mb-1">Ouvrez l'application Wave</p>
                    <p className="text-purple-700">Sur votre téléphone mobile</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-purple-100 text-purple-800 rounded-lg w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">2</div>
                  <div>
                    <p className="font-semibold text-purple-800 text-lg mb-1">Scannez le QR code</p>
                    <p className="text-purple-700">Appuyez sur "Scanner" dans Wave et pointez la caméra</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-purple-100 text-purple-800 rounded-lg w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">3</div>
                  <div>
                    <p className="font-semibold text-purple-800 text-lg mb-1">Confirmez le paiement</p>
                    <p className="text-purple-700">Vérifiez le montant (10,000 FCFA) et validez</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-purple-100 text-purple-800 rounded-lg w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">4</div>
                  <div>
                    <p className="font-semibold text-purple-800 text-lg mb-1">Activation automatique</p>
                    <p className="text-purple-700">Votre abonnement est activé immédiatement</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 pt-6 border-t border-purple-200">
                <div className="flex items-center text-green-600 font-medium">
                  <CheckCircle className="mr-2" size={20} />
                  <span>Pas de confirmation manuelle nécessaire</span>
                </div>
                <p className="text-sm text-purple-600 mt-2">
                  La validation se fait directement par téléphone via Wave
                </p>
              </div>
            </div>
            
            {/* Informations importantes */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
              <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                <AlertCircle size={20} className="mr-2 text-blue-600" />
                Informations importantes
              </h4>
              <ul className="text-blue-700 space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Montant fixe : <strong>10,000 FCFA</strong></span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Durée : <strong>30 jours</strong> d'abonnement</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Référence : <span className="font-mono bg-blue-100 px-2 py-1 rounded text-xs">JANGAL_{schoolId?.substring(0, 8) || 'SCHOOL'}</span></span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Destinataire : Votre compte Wave marchand (773620212)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Colonne droite : QR Code */}
          <div className="flex flex-col items-center justify-center">
            {/* Titre QR Code */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2 flex items-center justify-center">
                <QrCode className="mr-3 text-purple-600" size={28} />
                QR Code Wave
              </h3>
              <p className="text-gray-600">Scannez avec l'application Wave</p>
            </div>

            {/* Zone QR Code */}
            <div className="relative bg-white p-8 rounded-2xl shadow-xl border-2 border-purple-300 mb-6">
              <div className="flex items-center justify-center mb-4">
                <img 
                  src={waveQrCodeUrl} 
                  alt="QR Code Wave pour paiement"
                  className="w-72 h-72 object-contain rounded-lg"
                  onError={handleImageError}
                  crossOrigin="anonymous"
                />
              </div>
              
              {qrError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-700 text-sm flex items-center">
                    <AlertCircle size={18} className="mr-3 flex-shrink-0" />
                    {qrError}
                  </p>
                </div>
              )}
              
              <div className="mt-6 flex items-center justify-center text-purple-700 font-semibold">
                <Smartphone className="mr-2 animate-bounce" size={20} />
                <span>Scan avec l'application Wave</span>
              </div>
            </div>

            {/* Bouton Voir en grand */}
            <button
              onClick={() => window.open(waveQrCodeUrl, '_blank')}
              className="w-full mb-8 flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition duration-200 shadow-lg hover:shadow-xl"
            >
              <QrCode size={20} className="mr-2" />
              <span className="font-semibold">Voir le QR code en grand</span>
            </button>
            
            {/* Boutons d'action */}
            <div className="w-full flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  navigator.clipboard.writeText('773620212');
                  alert('Numéro Wave copié : 773620212');
                }}
                className="flex items-center justify-center px-5 py-3 border-2 border-purple-600 text-purple-600 rounded-xl hover:bg-purple-50 transition duration-200 font-medium"
              >
                <Copy size={18} className="mr-2" />
                Copier le numéro Wave
              </button>
              
              <button
                onClick={() => window.open('https://www.wave.com/senegal/', '_blank')}
                className="flex items-center justify-center px-5 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition duration-200 font-medium"
              >
                <ExternalLink size={18} className="mr-2" />
                Télécharger Wave
              </button>
            </div>
          </div>
        </div>

        {/* Footer avec support */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div className="mb-4 md:mb-0">
              <p className="text-gray-600 font-medium mb-2">
                Besoin d'aide ? Contactez le support :
              </p>
              <div className="space-y-1">
                <p className="font-semibold text-purple-700">Wave : 200 600</p>
                <p className="text-sm text-gray-500">Support technique Wave (appel gratuit)</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              <div className="flex items-center mb-1">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <p>Paiement 100% sécurisé par Wave</p>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <p>Validation automatique après scan</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">Tout paiement est soumis aux conditions générales de Wave</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaveQRPaymentSimple;